// Shared timer (M55.1, ARCH §30.3, COW-4). PURE (ENG-1). A timer counts down
// from the SIM CLOCK, never a wall-clock message — every client agrees with no
// sync frame, exactly the way `world.tickCount` already drives every other
// synchronized display.
import { COW } from '../config';

export type TimerPreset = 'pomodoro25' | 'break5' | 'custom';

export interface TimerData {
  endTick: number | null;
  preset: TimerPreset;
  customTicks?: number;
}

/** Ticks a preset runs for; `custom` clamps to `COW.TIMER_MAX` (E-63: clamp,
 *  never refuse). */
export function presetTicks(preset: TimerPreset, customTicks?: number): number {
  if (preset === 'pomodoro25') return COW.POMODORO;
  if (preset === 'break5') return COW.BREAK;
  return Math.max(0, Math.min(COW.TIMER_MAX, customTicks ?? 0));
}

/** Start (or restart) a timer at `nowTick`. */
export function startTimer(nowTick: number, preset: TimerPreset, customTicks?: number): TimerData {
  const ticks = presetTicks(preset, customTicks);
  return { endTick: nowTick + ticks, preset, ...(preset === 'custom' ? { customTicks: ticks } : {}) };
}

/** Stop a running timer (its face reads blank/idle). */
export function stopTimer(timer: TimerData): TimerData {
  return { ...timer, endTick: null };
}

/** Ticks remaining, or null if not running (E-63-adjacent: never negative). */
export function ticksRemaining(timer: TimerData, nowTick: number): number | null {
  if (timer.endTick === null) return null;
  return Math.max(0, timer.endTick - nowTick);
}

/** True on the EXACT tick the timer reaches zero (not "at or past" — a tick
 *  comparison over stored state, so a missed tick never double-announces on
 *  the next check; the caller fires the announcement only on this edge). */
export function justFinished(timer: TimerData, nowTick: number): boolean {
  return timer.endTick !== null && timer.endTick === nowTick;
}

/** `mm:ss` face text, or the idle string when not running. */
export function timerFace(timer: TimerData, nowTick: number): string {
  const rem = ticksRemaining(timer, nowTick);
  if (rem === null) return '--:--';
  const totalSeconds = Math.ceil(rem / 20); // TPS
  const m = Math.floor(totalSeconds / 60), s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
